import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service';
import { isString } from '@gov-nx/core/types';
import type { ToastMessageSubscription } from './message.types';
import {
	messageEvents,
	MessageSubscriptionTypes,
	toastMessageSubscription,
} from './messageEvents';

interface MessageEventsProps {
	onToastMessage?: (
		action: ToastMessageSubscription['type'],
		payload: ToastMessageSubscription['payload']
	) => void;
}

const common = {
	type: 'bold',
	gravity: 'bottom',
	position: 'center',
	time: 10_000,
} as const;

export const useMessageEvents = ({
	onToastMessage,
}: MessageEventsProps = {}) => {
	const { t } = useTranslation(LocalizeNameSpaceTypes.Global);

	useEffect(() => {
		const toastMessageSub =
			onToastMessage &&
			messageEvents.subscribe(
				MessageSubscriptionTypes.toastMessage,
				onToastMessage
			);

		return () => {
			toastMessageSub?.unSubscribe();
		};
	}, [onToastMessage]);

	const toastMessage = (payload: ToastMessageSubscription['payload']) => {
		messageEvents.publish(toastMessageSubscription(payload));
	};

	return {
		toastMessage,
		toastMessageSuccess: (
			content: string,
			options?: ToastMessageSubscription['payload']['options'],
			links?: ToastMessageSubscription['payload']['links']
		) =>
			toastMessage({
				links,
				options: {
					...common,
					...options,
					color: 'success',
					icon: {
						name: 'check-lg',
						type: 'components',
					},
				},
				content,
			}),
		toastMessageWarning: (content: string) =>
			toastMessage({
				options: {
					...common,
					color: 'warning',
					icon: {
						name: 'lightbulb-fill',
						type: 'components',
					},
				},
				content,
			}),
		toastMessageError: (
			content?: string | null,
			options?: ToastMessageSubscription['payload']['options']
		) =>
			toastMessage({
				options: {
					...common,
					...options,
					color: 'error',
					icon: {
						name: 'exclamation-triangle-fill',
						type: 'components',
					},
				},
				content: isString(content) ? content : t('notification.error.default'),
			}),
		toastMessageNeutral: (
			content: string,
			options?: ToastMessageSubscription['payload']['options']
		) =>
			toastMessage({
				options: {
					...common,
					...options,
					color: 'neutral',
				},
				content,
			}),
		toastMessagePrimary: (
			content: string,
			options?: ToastMessageSubscription['payload']['options']
		) =>
			toastMessage({
				options: {
					...common,
					...options,
					color: 'primary',
				},
				content,
			}),
	};
};
